import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { CustomerStatusBadge } from '../ui';
import { customersService } from '../../services/customers.service';
import { avatarInitials } from '../customers/CustomersList';

/* ── Avatar helpers (same logic as CustomersList) ──────────────────────────── */
const AVATAR_COLORS = [
  'bg-accent-100 text-accent-700',
  'bg-success-100 text-success-700',
  'bg-info-100 text-info-700',
  'bg-warning-100 text-warning-800',
  'bg-danger-100 text-danger-700',
];
const avatarColor = (id) => AVATAR_COLORS[(id?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

const maskContact = (value) => {
  if (!value) return null;
  if (value.includes('@')) {
    const [local, domain] = value.split('@');
    return `${local.slice(0, 2)}***@${domain}`;
  }
  return `${value.slice(0, 4)}****${value.slice(-2)}`;
};

/* ── Skeleton shimmer ────────────────────────────────────────────────────── */
const PopoverSkeleton = () => (
  <div className="space-y-3 animate-pulse" data-testid="popover-skeleton">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-ink-100 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 w-32 bg-ink-100 rounded" />
        <div className="h-2.5 w-20 bg-ink-100 rounded" />
      </div>
    </div>
    <div className="h-2.5 w-40 bg-ink-100 rounded" />
    <div className="h-2.5 w-24 bg-ink-100 rounded" />
  </div>
);

/* ── Popover content ─────────────────────────────────────────────────────── */
const PopoverContent = ({ customer, customerId }) => {
  const initials = avatarInitials(customer.firstName, customer.lastName, customer.email);
  const color = avatarColor(customerId);
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || 'Unknown';
  const contact = customer.email || customer.phone;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${color}`}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-900 truncate">{fullName}</p>
          <CustomerStatusBadge status={customer.status} />
        </div>
      </div>

      <p className="text-xs font-mono text-ink-600 break-all select-all">{customerId}</p>

      {contact && (
        <p className="text-xs text-ink-500 truncate">{maskContact(contact)}</p>
      )}

      <Link
        to={`/customers/${customerId}`}
        className="flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-800 transition-colors"
        data-testid="view-customer-link"
      >
        View customer <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );
};

/* ── Main component ──────────────────────────────────────────────────────── */
const POPOVER_HEIGHT_ESTIMATE = 200;

const CustomerHoverCard = ({ customerId, children }) => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const triggerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const cacheRef = useRef(null);

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const above = spaceBelow < POPOVER_HEIGHT_ESTIMATE + 12;
    const top = above
      ? rect.top + window.scrollY - POPOVER_HEIGHT_ESTIMATE - 8
      : rect.bottom + window.scrollY + 8;
    const left = Math.max(
      8,
      Math.min(rect.left + window.scrollX, window.innerWidth + window.scrollX - 268),
    );
    setPosition({ top, left });
  }, []);

  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;
    if (cacheRef.current) {
      setCustomer(cacheRef.current);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = await customersService.getCustomer(customerId);
      cacheRef.current = data;
      setCustomer(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const handleOpen = useCallback(() => {
    if (!customerId) return;
    clearTimeout(hideTimerRef.current);
    computePosition();
    setOpen(true);
    setMounted(true);
    fetchCustomer();
  }, [customerId, computePosition, fetchCustomer]);

  const handleClose = useCallback(() => {
    hideTimerRef.current = setTimeout(() => setOpen(false), 150);
  }, []);

  const handlePopoverEnter = useCallback(() => {
    clearTimeout(hideTimerRef.current);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        onKeyDown={handleKeyDown}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>

      {mounted && open && customerId
        ? createPortal(
            <div
              role="tooltip"
              onMouseEnter={handlePopoverEnter}
              onMouseLeave={handleClose}
              style={{
                position: 'absolute',
                top: position.top,
                left: position.left,
                zIndex: 9999,
                opacity: open ? 1 : 0,
                transition: 'opacity 150ms ease',
              }}
              className="bg-white rounded-xl shadow-xl border border-gray-100 p-4 min-w-[240px] max-w-[300px]"
              data-testid="customer-hover-card"
            >
              {loading && !customer ? (
                <PopoverSkeleton />
              ) : error ? (
                <p className="text-xs text-ink-500">Could not load customer</p>
              ) : customer ? (
                <PopoverContent customer={customer} customerId={customerId} />
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default CustomerHoverCard;
